/**
 * Lab UserProfile account_role helpers (admin | staff | viewer).
 * Suppliers use is_supplier; they have no account_role gating here.
 *
 * A Viewer is an external reviewer: same reads as Staff, no writes. The backend
 * rejects every POST/PUT/PATCH/DELETE from a Viewer with 403 (except login and
 * logout), so the UI only has to hide controls that can only fail.
 */

/** @typedef {'admin' | 'staff' | 'viewer'} LabAccountRole */

/**
 * Normalize account_role from login/detail payloads.
 * Missing or unknown values default to staff for safety.
 *
 * @param {Object|null|undefined} userDetails
 * @returns {LabAccountRole|null} null when supplier / empty details (no lab role)
 */
export function getAccountRole(userDetails) {
  if (!userDetails || typeof userDetails !== 'object') {
    return null;
  }
  if (userDetails.is_supplier === true) {
    return null;
  }
  const raw = String(userDetails.account_role || '')
    .trim()
    .toLowerCase();
  if (raw === 'admin') {
    return 'admin';
  }
  if (raw === 'viewer') {
    return 'viewer';
  }
  return 'staff';
}

/**
 * True when the current session is a lab Admin (not a supplier).
 *
 * @param {Object|null|undefined} userDetails
 * @param {boolean} [isSupplier=false]
 * @returns {boolean}
 */
export function isLabAdmin(userDetails, isSupplier = false) {
  if (isSupplier) {
    return false;
  }
  return getAccountRole(userDetails) === 'admin';
}

/**
 * True when the current session is a read-only lab Viewer (not a supplier).
 *
 * Prefers the `is_lab_viewer` boolean from `user_details` so a future role does
 * not silently get write buttons; falls back to `account_role` for sessions
 * stored before the backend started sending the boolean.
 *
 * @param {Object|null|undefined} userDetails
 * @param {boolean} [isSupplier=false]
 * @returns {boolean}
 */
export function isLabViewer(userDetails, isSupplier = false) {
  if (isSupplier || !userDetails || typeof userDetails !== 'object') {
    return false;
  }
  if (userDetails.is_supplier === true) {
    return false;
  }
  if (userDetails.is_lab_viewer === true) {
    return true;
  }
  return getAccountRole(userDetails) === 'viewer';
}

/**
 * Human-readable label for display chips.
 *
 * @param {Object|null|undefined} userDetails
 * @param {boolean} [isSupplier=false]
 * @returns {string}
 */
export function getAccountRoleLabel(userDetails, isSupplier = false) {
  if (isSupplier || userDetails?.is_supplier) {
    return 'Supplier';
  }
  if (isLabViewer(userDetails, isSupplier)) {
    return 'Viewer';
  }
  const role = getAccountRole(userDetails);
  if (role === 'admin') {
    return 'Admin';
  }
  return 'Staff';
}

/**
 * Drop read-only Viewers from a lab user list.
 *
 * `GET /users/` still returns Viewers so Admins can manage them, but the backend
 * rejects a Viewer id as a recipient/assignee with 400. Pickers filter here.
 *
 * @param {Array<Object>|null|undefined} users
 * @returns {Array<Object>} users without Viewers (empty array for non-arrays)
 */
export function excludeLabViewers(users) {
  if (!Array.isArray(users)) {
    return [];
  }
  return users.filter(user => !isLabViewer(user));
}
